"use client";

import * as React from "react";
import Image from "next/image";
import {
    Box,
    Button,
    Chip,
    Container,
    FormControl,
    Grid,
    InputAdornment,
    InputBase,
    MenuItem,
    Select,
    Skeleton,
    Stack,
    ToggleButton,
    ToggleButtonGroup,
    Typography,
} from "@mui/material";
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import CarInvestmentCard, { CarInvestmentCardProps } from "./PickYourCarInvestmentCard";
import Heading from "../heading";
import SearchIcon from "@mui/icons-material/Search";
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";
import IconButton from "@mui/material/IconButton";
import EnquireFormModal from '../../components/all-services/enquiryForm'
import CtaButton from "../buttonWithArrow";
import AddIcon from '@mui/icons-material/Add';

const INITIAL_STATE = {
    vehicle: '',
    initial: '',
    monthly: '',
    month: ''
}

const SkeletonLoader = () => {
    return (
        <Grid container spacing={3}>
            {[...Array(6)].map((_, index) => (
                <Grid item xs={12} sm={4} key={index}>
                    <Box
                        sx={{
                            backgroundColor: '#fff',
                            borderRadius: 2,
                            boxShadow: 2,
                            padding: 2,
                        }}
                    >
                        {/* Image Skeleton */}
                        <Skeleton variant="rectangular" width="100%" height={200} />

                        {/* Car Title */}
                        <Skeleton sx={{ marginTop: 2, marginBottom: 1 }} height={30} width="60%" />

                        {/* Car Information (Down Payment, Income, Installment) */}
                        <Stack spacing={1} sx={{ marginTop: 2 }}>
                            <Skeleton height={20} width="80%" />
                            <Skeleton height={20} width="80%" />
                            <Skeleton height={20} width="80%" />
                        </Stack>

                        {/* Price and ROI */}
                        <Stack spacing={1} sx={{ marginTop: 2 }}>
                            <Skeleton height={30} width="50%" />
                            <Skeleton height={30} width="50%" />
                        </Stack>

                        {/* Action Buttons (Book a Call, Send a Message) */}
                        <Stack direction="row" spacing={2} sx={{ marginTop: 2 }}>
                            <Skeleton variant="rectangular" width="45%" height={40} />
                            <Skeleton variant="rectangular" width="45%" height={40} />
                        </Stack>

                        {/* Expense Breakdown */}
                        <Skeleton sx={{ marginTop: 2 }} height={25} width="60%" />
                    </Box>
                </Grid>
            ))}
        </Grid>
    );
};

export default function PickYourCarGrid({ name, loader = false, data, endIconSrc, onDown, orderJet, costCalculate }) {
    const { title, subtitle, carData, type, options } = data;
    const [filter, setFilter] = React.useState("All");
    const [contact, setContact] = React.useState(INITIAL_STATE)
    const [openEnquire, setOpenEnquire] = React.useState(false)
    const handleOpenEnquire = () => setOpenEnquire(true)
    const handleCloseEnquire = () => setOpenEnquire(false)
    const [selectedVehicle, setSelectedVehicle] = React.useState('')
    const [vehicleInfo, setVehicleInfo] = React.useState([])
    const [expandedId, setExpandedId] = React.useState(null);
    const [search, setSearch] = React.useState("");
    const list = React.useMemo(() => {
        const q = search.trim().toLowerCase();
        return (filter === "All" ? carData : carData.filter(c => c.segment === filter))
            .filter((c) => {
                if (!q) return true;
                // Build a simple haystack from common fields
                const hay = [
                    c.title,
                    c.name,
                    c.segment,
                    c.brand,
                    c.model,
                ].filter(Boolean).join(" ").toLowerCase();
                return hay.includes(q);
            });
    }, [carData, filter, search]);

    React.useEffect(() => { setExpandedId(null); }, [filter, search]);

    const HEADER_OFFSET = 88;
    const scrollToId = (id) => {
        const el = document.getElementById(id);
        if (!el) return;
        const y = el.getBoundingClientRect().top + window.pageYOffset - HEADER_OFFSET;
        window.scrollTo({ top: y, behavior: "smooth" });
    };

    // Extract segments, remove duplicates, and add "All" at the start
    const uniqueSegments = [
        "All",
        ...[...new Set(carData?.map(item => item?.segment))]  // Map and remove duplicates
    ];

    return (
        <Box sx={{ py: { xs: 6, md: 4 } }}>
            <Container sx={{ maxWidth: "1320px !important" }}>
                {/* Heading */}
                <Heading title={title} subtitle={subtitle} />

                {/* Toggle */}
                {/* Responsive Toggle */}
                {carData?.length > 0 && (
                    <Box
                        sx={{
                            // graceful horizontal scroll if there are many items
                            overflowX: "auto",
                            mx: { xs: -2, sm: 0 },            // bleed to edges on mobile
                            px: { xs: 2, sm: 0 },
                            "&::-webkit-scrollbar": { display: "none" },
                            scrollbarWidth: "none",
                            display: "flex",
                            justifyContent: "space-between",
                            flexDirection: { xs: "column-reverse", md: "row" }
                        }}
                    >
                        <Stack
                            direction="row"
                            justifyContent="center"
                            useFlexGap
                            flexWrap="wrap"                    // wrap on small screens
                            sx={{
                                gap: { xs: 1, sm: 1.25, md: 1.5 } // responsive spacing
                            }}
                        >
                            {
                                type === "jet" ?
                                    <FormControl sx={{ minWidth: 260 }}>
                                        <Select
                                            // value={value}
                                            // onChange={(e) => onChange(e.target.value)}
                                            displayEmpty
                                            IconComponent={KeyboardArrowDownIcon}
                                            renderValue={(selected) =>
                                                selected ? (
                                                    options.find((o) => o.value === selected)?.label
                                                ) : (
                                                    <Box component="span" sx={{ color: "#111827" }}>
                                                        Select Jet Ownership
                                                    </Box>
                                                )
                                            }
                                            sx={{
                                                borderRadius: 999,
                                                height: { xs: 47, md: 47 },
                                                bgcolor: "#f6f6f6",
                                                "& .MuiOutlinedInput-notchedOutline": { borderColor: "#eeeeee" },
                                                "&:hover .MuiOutlinedInput-notchedOutline": { borderColor: "#e5e5e5" },
                                                "&.Mui-focused .MuiOutlinedInput-notchedOutline": { borderColor: "#dcdcdc" },
                                                "& .MuiSelect-select": { py: 1, px: 2, fontWeight: 600 },
                                            }}
                                            MenuProps={{
                                                PaperProps: {
                                                    sx: { borderRadius: 2, boxShadow: "0 8px 20px rgba(0,0,0,.08)" },
                                                },
                                            }}
                                            inputProps={{ "aria-label": "Select Jet Ownership" }}
                                        >
                                            {options?.map((o) => (
                                                <MenuItem key={o.value} value={o.value}>
                                                    {o.label}
                                                </MenuItem>
                                            ))}
                                        </Select>
                                    </FormControl>
                                    :
                                    uniqueSegments?.map((t, index) => {
                                        const active = filter === t;
                                        return (
                                            <Chip
                                                key={index}
                                                clickable
                                                onClick={() => setFilter(t)}
                                                label={t}
                                                // keep your active look, but make sizes responsive
                                                sx={{
                                                    ...pillSx(active),
                                                    px: { xs: 1.25, sm: 1.5, md: 1.75 },
                                                    height: { xs: 36, sm: 36, md: 36 },
                                                    borderRadius: 999,
                                                    // responsive label sizing
                                                    "& .MuiChip-label": {
                                                        px: 0,
                                                        fontWeight: 600,
                                                        fontSize: { xs: 12, sm: 13, md: 14 },
                                                    },
                                                }}
                                            />
                                        );
                                    })
                            }

                        </Stack>
                        <Stack
                            direction="row"
                            justifyContent="center"
                            useFlexGap
                            flexWrap="wrap"                    // wrap on small screens
                            sx={{
                                gap: { xs: 1, sm: 1.25, md: 1.5 }, // responsive spacing
                                mt: { xs: 4, sm: 2, md: 0 },
                                mb: { xs: 4, sm: 4, md: 0 }
                            }}
                        >
                            {
                                type === "jet" ? <Button
                                    startIcon={<AddIcon sx={{ fontSize: 18 }} />}
                                    sx={{
                                        borderRadius: 999,
                                        width: { xs: 160, sm: 180, md: 200 },
                                        height: { xs: 47, md: 47 },
                                        px: 2.25,
                                        textTransform: "none",
                                        fontWeight: 700,
                                        color: "#0D8196",
                                        // gradient border trick
                                        border: "2px solid transparent",
                                        background:
                                            "linear-gradient(#fff, #fff) padding-box, linear-gradient(135deg, #81D0D5, #0D8196) border-box",
                                        boxShadow: "0 1px 2px rgba(13,129,150,.12)",
                                        "&:hover": {
                                            background:
                                                "linear-gradient(#f9ffff, #f9ffff) padding-box, linear-gradient(135deg, #81D0D5, #0D8196) border-box",
                                        },
                                    }}
                                    onClick={orderJet}
                                >
                                    Order Jet
                                </Button> :
                                    <InputBase
                                        type="search"
                                        value={search}
                                        onChange={(e) => setSearch(e.target.value)}
                                        placeholder="Search.."
                                        endAdornment={
                                            search ? (
                                                <InputAdornment position="end">
                                                    <IconButton
                                                        size="small"
                                                        aria-label="Clear search"
                                                        onClick={() => setSearch("")}
                                                    >
                                                        <CloseRoundedIcon sx={{ fontSize: 18 }} />
                                                    </IconButton>
                                                </InputAdornment>
                                            ) : <InputAdornment position="start">
                                                <SearchIcon sx={{ fontSize: 18, color: "#707379" }} />
                                            </InputAdornment>
                                        }
                                        sx={{
                                            width: { xs: 160, sm: 180, md: 200 },
                                            height: { xs: 47, md: 47 },
                                            px: 1,
                                            borderRadius: 999,
                                            bgcolor: "#fff",
                                            border: "1px solid #E5E7EB",
                                            boxShadow: "0 1px 2px rgba(0,0,0,0.04)",
                                        }}
                                    />
                            }
                            <CtaButton label="Cost Calculator" onClick={costCalculate} />
                        </Stack>
                    </Box>
                )}

                {
                    loader ? <Box sx={{ mt: 2 }}><SkeletonLoader /> </Box> :
                        <Grid container spacing={4} sx={{ mt: 2 }} alignItems="flex-start">
                            {list?.map((car) => (
                                <Grid key={car._id} item xs={12} sm={6} md={4}>
                                    <CarInvestmentCard
                                        {...car}
                                        openBreakdown={expandedId === car._id}
                                        onToggle={() =>
                                            setExpandedId(expandedId === car._id ? null : car._id)
                                        }
                                        FleetData={list}
                                        type={type}
                                        setSelectedVehicle={setSelectedVehicle}
                                        setVehicleInfo={setVehicleInfo}
                                        vehicleInfo={vehicleInfo}
                                        setContact={setContact}
                                        selectedVehicle={selectedVehicle}
                                        contact={contact}
                                        setOpenEnquire={setOpenEnquire}
                                        name={name}
                                    />
                                </Grid>
                            ))}
                            {list?.length === 0 && (
                                <Grid item xs={12}>
                                    {
                                        search ?
                                            <Box sx={{ textAlign: "center", color: "#6b7280", py: 4 }}>
                                                No cars match “{search}”.
                                            </Box> :
                                            <Box sx={{ textAlign: "center", color: "#6b7280", py: 4 }}>
                                                No cars match.
                                            </Box>
                                    }

                                </Grid>
                            )}

                            {carData?.length > 0 && (
                                <Grid item xs={12} sx={{ display: "flex", justifyContent: "center" }}>
                                    <CtaButton label="Investment Breakdown" onClick={() => scrollToId(onDown)} endIconSrc={endIconSrc ?? undefined} />
                                    {/* <CtaButton cta={{ label: "Investment Breakdown", href: onDown ? onDown : "#" }} endIconSrc={endIconSrc ?? undefined} /> */}
                                </Grid>
                            )}

                        </Grid>
                }

            </Container>
        </Box>
    );
}

const pillSx = (active) => ({
    flexShrink: 0,
    fontWeight: 700,
    minWidth: "150px",
    borderRadius: "999px",
    px: { xs: 1.5, md: 2.5 },
    py: { xs: 1, md: 3 },
    fontSize: { xs: 14, md: 16 },
    background: active
        ? "linear-gradient(180deg, #75c4c5 0%, #0d8196 100%)"
        : "#f1f1f1",
    color: active ? "#fff" : "#000",
    "&:hover": {
        background: active
            ? "linear-gradient(180deg, #4bb6b4 0%, #249f9c 100%)"
            : "#f7f9f9",
    },
    "& .MuiChip-label": { px: 0, fontWeight: 700 },
});